import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { UploadSimple } from "@phosphor-icons/react";

export default function Settings() {
  const { org, refresh, user } = useAuth();
  const [form, setForm] = useState({ name: "", brand_color: "#0D9488", domain: "" });
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const logoInput = useRef(null);

  useEffect(() => {
    if (org) setForm({ name: org.name, brand_color: org.brand_color || "#0D9488", domain: org.domain || "" });
    api.get("/org/members").then((r) => setMembers(r.data)).catch(() => {});
  }, [org]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/org", form);
      await refresh();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/org/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Logo updated");
    } catch (e) { toast.error("Upload failed"); }
  };

  const inputCls = "w-full bg-card border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm";

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="settings-page">
      <PageHeader eyebrow="Settings" title="Workspace & White-label" subtitle="Brand the platform for your organisation. Visible to every member." />

      <div className="mt-8 space-y-6">
        <section className="bg-card border border-border rounded-2xl p-5 sm:p-6 card-elev">
          <div className="text-overline text-muted-foreground mb-4">Organisation</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-medium mb-2 block">Organisation name</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} data-testid="settings-org-name" />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Custom domain</label>
              <input value={form.domain} onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))} placeholder="intel.yourfirm.com" className={inputCls} data-testid="settings-domain" />
            </div>
          </div>
        </section>

        <section className="bg-card border border-border rounded-2xl p-5 sm:p-6 card-elev">
          <div className="text-overline text-muted-foreground mb-4">Brand</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-medium mb-2 block">Brand colour</label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.brand_color} onChange={(e) => setForm((f) => ({ ...f, brand_color: e.target.value }))} className="h-11 w-16 border border-border rounded-lg cursor-pointer" data-testid="settings-color" />
                <input value={form.brand_color} onChange={(e) => setForm((f) => ({ ...f, brand_color: e.target.value }))} className="flex-1 bg-card border border-border rounded-lg px-3 py-2.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Logo</label>
              <div className="flex items-center gap-3">
                {org?.logo_url ? (
                  <img src={org.logo_url.startsWith('http') ? org.logo_url : `${process.env.REACT_APP_BACKEND_URL}${org.logo_url}`} alt="logo" className="h-11 w-11 object-contain border border-border rounded-lg bg-card" />
                ) : (
                  <div className="h-11 w-11 border border-dashed border-border rounded-lg grid place-items-center text-xs text-muted-foreground">none</div>
                )}
                <button onClick={() => logoInput.current?.click()} className="inline-flex items-center gap-2 border border-border bg-card rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-secondary transition-colors" data-testid="settings-logo-btn">
                  <UploadSimple size={14} weight="bold" /> Upload logo
                </button>
                <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="settings-logo-input" />
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button disabled={saving} onClick={save} className="bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 card-elev" data-testid="settings-save">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </section>

        <section className="bg-card border border-border rounded-2xl p-5 sm:p-6 card-elev">
          <div className="text-overline text-muted-foreground mb-4">Team</div>
          <div className="space-y-2">
            {members.length === 0 && <div className="text-sm text-muted-foreground">No other members.</div>}
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3 bg-secondary rounded-lg" data-testid={`member-${m.id}`}>
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary to-[hsl(var(--info))] text-white grid place-items-center text-sm font-bold">{m.full_name?.[0]?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{m.full_name} {m.id === user?.id && <span className="text-xs text-muted-foreground font-normal">(you)</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                </div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-teal bg-teal-soft px-2 py-1 rounded-full">{m.role}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">Team invites and SSO are available on the Enterprise plan.</p>
        </section>

        <section className="bg-card border border-border rounded-2xl p-5 sm:p-6 card-elev">
          <div className="text-overline text-muted-foreground mb-4">Current plan</div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-display text-xl capitalize">{org?.plan || "discovery"}</div>
              <div className="text-sm text-muted-foreground">Upgrade for unlimited assessments, white-label and SSO.</div>
            </div>
            <a href="/pricing" className="border border-border bg-card rounded-lg px-4 py-2 text-sm font-medium hover:bg-secondary transition-colors" data-testid="settings-upgrade">See plans</a>
          </div>
        </section>

        <section className="bg-card border border-border rounded-2xl p-5 sm:p-6 card-elev" data-testid="settings-tour-section">
          <div className="text-overline text-muted-foreground mb-4">Product tour</div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-display text-xl">Replay the onboarding tour</div>
              <div className="text-sm text-muted-foreground">A 60-second walk-through of Workspaces, Radar, mandates and the AI Analyst.</div>
            </div>
            <button
              onClick={() => { if (window.__orixa_start_tour) window.__orixa_start_tour(); else toast.info("Tour will be available after page reload."); }}
              className="border border-border bg-card rounded-lg px-4 py-2 text-sm font-medium hover:bg-secondary transition-colors"
              data-testid="settings-replay-tour"
            >
              Replay tour
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
